/**
 * Add a line the app missed (Phase A, B17, 32.7) — beside "What the app read".
 *
 * The officer points at a sentence of the paper, says what kind of line it is,
 * gives one English line and the reason. Four things are the product:
 *
 * **The words are the page's.** The page's own text is shown beside the form;
 * the officer selects the words there and takes them — an English keyboard
 * cannot retype a Hindi sentence, and a retyped one is a different sentence.
 * The server checks them against the page and keeps the whole sentence they
 * stand in, and says so.
 *
 * **What may be added is the server's.** Which kinds of line this paper takes —
 * an allegation, a fact, a dated event, his answer to an article — and why one
 * is not offered come off the API. The quasi-judicial papers take only their
 * own words (ADR-021): no allegation, and no English line of the officer's.
 *
 * **Nothing is overwritten.** It is an officer's entry in the record, with who
 * and why, beside the app's lines; the next draft reads it like any other.
 *
 * **A blocked add stays on screen**, greyed, with the reason (rule 3).
 */

import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { casePageApi } from '../../api/client'
import type { AddRecordLineBody, LineKindOption, SourceFileView } from '../../api/client'
import { ErrorEnvelope } from '../../components/bits'
import './reading.css'

type Stance = NonNullable<AddRecordLineBody['stance']>

/** His stance on an article, as he puts it — the values the API takes. */
const STANCES: { key: Stance; label: string }[] = [
  { key: 'denies', label: 'Denies it' },
  { key: 'admits', label: 'Admits it' },
  { key: 'admits-in-part', label: 'Admits it in part' },
  { key: 'explains', label: 'Explains' },
  { key: 'other', label: 'Something else' },
]

export function AddALine({ caseId, row }: { caseId: string; row: SourceFileView }) {
  const [open, setOpen] = useState(false)
  const [said, setSaid] = useState<string | null>(null)
  if (row.removed_at) return null
  return (
    <div className="add-line" data-testid={`add-line-${row.ordinal}`}>
      {open ? (
        <AddLineForm
          caseId={caseId}
          row={row}
          onDone={(message) => {
            setOpen(false)
            setSaid(message)
          }}
        />
      ) : (
        <p className="small">
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              setSaid(null)
              setOpen(true)
            }}
            data-testid={`open-add-line-${row.ordinal}`}
          >
            Add a line the app missed
          </button>
          {said ? <span className="muted"> {said}</span> : null}
        </p>
      )}
    </div>
  )
}

function AddLineForm({
  caseId,
  row,
  onDone,
}: {
  caseId: string
  row: SourceFileView
  onDone: (message: string | null) => void
}) {
  const client = useQueryClient()
  const pages = useQuery({
    queryKey: ['paper-pages', caseId, row.id],
    queryFn: () => casePageApi.getPaperPages(caseId, row.id),
  })
  const [kind, setKind] = useState<LineKindOption['key'] | null>(null)
  const [page, setPage] = useState<number | null>(null)
  const [quote, setQuote] = useState('')
  const [english, setEnglish] = useState('')
  const [reason, setReason] = useState('')
  const [number, setNumber] = useState('')
  const [dated, setDated] = useState('')
  const [stance, setStance] = useState<Stance | ''>('')
  const [noSelection, setNoSelection] = useState(false)
  const textRef = useRef<HTMLPreElement>(null)

  const view = pages.data
  const kinds = view?.line_kinds ?? []
  const option = kinds.find((k) => k.key === kind) ?? null
  const shown = view?.pages ?? []
  const wordFile = view?.text_origin === 'DOCX_UPLOAD'
  const current = shown.find((p) => p.page === page) ?? shown[0] ?? null

  const add = useMutation({
    mutationFn: () => {
      const body: AddRecordLineBody = {
        preflight: false,
        kind: option?.key ?? 'fact',
        page: wordFile ? null : (current?.page ?? null),
        quote: quote.trim(),
        reason: reason.trim(),
        english: option?.takes_english ? english.trim() : null,
        number: option && option.number !== 'none' && number.trim() ? Number(number) : null,
        date_as_written: option?.takes_date ? dated.trim() : null,
        stance: option?.takes_stance && stance ? stance : null,
      }
      return casePageApi.addRecordLine(caseId, row.id, body)
    },
    onSuccess: (receipt) => {
      void client.invalidateQueries({ queryKey: ['record', caseId] })
      void client.invalidateQueries({ queryKey: ['papers', caseId] })
      void client.invalidateQueries({ queryKey: ['case', caseId] })
      onDone(receipt.message)
    },
  })

  if (pages.isLoading) return <p className="small muted">Reading the paper’s pages…</p>
  if (pages.error || !view) {
    return (
      <div>
        <ErrorEnvelope error={pages.error} />
        <button type="button" className="btn ghost" onClick={() => onDone(null)}>
          Close
        </button>
      </div>
    )
  }
  if (!view.can_add) {
    return (
      <div className="blocked unsettled" data-testid={`add-line-blocked-${row.ordinal}`}>
        <div>
          <button type="button" className="btn ghost" disabled>
            Add a line the app missed
          </button>
          <div className="why">{view.cannot_add_reason}</div>
          <button type="button" className="btn ghost" onClick={() => onDone(null)}>
            Close
          </button>
        </div>
      </div>
    )
  }

  const takeSelection = () => {
    const selection = window.getSelection()
    const words = selection?.toString().trim() ?? ''
    const inside =
      selection && selection.rangeCount > 0 && textRef.current
        ? textRef.current.contains(selection.getRangeAt(0).commonAncestorContainer)
        : false
    if (!words || !inside) {
      setNoSelection(true)
      return
    }
    setNoSelection(false)
    setQuote(words.replace(/\s+/g, ' '))
  }

  const missing = !option
    ? 'Say what kind of line it is.'
    : !quote.trim()
      ? 'Take the words from the page.'
      : option.number === 'required' && !number.trim()
        ? `Say ${(option.number_label ?? 'the number').toLowerCase()}.`
        : option.takes_date && !dated.trim()
          ? 'Give its date as the page writes it.'
          : option.takes_english && !english.trim()
            ? 'Give one English line saying what it is.'
            : !reason.trim()
              ? 'Say why it is added.'
              : null

  return (
    <div className="add-line-form" data-testid={`add-line-form-${row.ordinal}`}>
      <fieldset className="add-line-kinds">
        <legend className="small muted">What kind of line is it?</legend>
        {kinds.map((k) => (
          <label key={k.key} className={k.allowed ? '' : 'muted'} title={k.reason ?? undefined}>
            <input
              type="radio"
              name={`line-kind-${row.id}`}
              aria-label={k.label}
              disabled={!k.allowed}
              checked={kind === k.key}
              onChange={() => setKind(k.key)}
            />{' '}
            {k.label}
            {!k.allowed && k.reason ? <span className="small"> — {k.reason}</span> : null}
          </label>
        ))}
      </fieldset>

      {shown.length > 1 && !wordFile ? (
        <label className="small muted">
          Page{' '}
          <select
            aria-label="Page"
            value={current?.page ?? ''}
            onChange={(event) => setPage(Number(event.target.value))}
          >
            {shown.map((p) => (
              <option key={p.page} value={p.page}>
                {p.page}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {current ? (
        <div className="page-text-wrap">
          <p className="small muted">
            {wordFile ? 'The Word file’s text' : `Page ${current.page}, as the app has it`} —
            select the words, then take them.
          </p>
          <pre ref={textRef} className="page-text deva" data-testid="page-text">
            {current.text}
          </pre>
          {(view.unread_pages ?? []).length ? (
            <p className="small muted">
              Pages whose text is not known: {(view.unread_pages ?? []).join(', ')}.
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="row">
        <button type="button" className="btn ghost" onClick={takeSelection}>
          Take the selected words
        </button>
        {noSelection ? (
          <span className="small muted">Select the words in the page’s text first.</span>
        ) : null}
      </div>
      <label>
        The words, as on the page
        <textarea
          aria-label="The words, as on the page"
          className="deva"
          rows={3}
          value={quote}
          onChange={(event) => setQuote(event.target.value)}
        />
      </label>
      <p className="small muted">
        They are checked against the page and kept as the whole sentence they stand in.
      </p>

      {option && option.number !== 'none' ? (
        <label>
          {option.number_label}
          {option.number === 'optional' ? ' (if any)' : ''}
          <input
            aria-label={option.number_label ?? 'Number'}
            inputMode="numeric"
            value={number}
            onChange={(event) => setNumber(event.target.value.replace(/[^0-9]/g, ''))}
          />
        </label>
      ) : null}
      {option?.takes_date ? (
        <label>
          Its date, as the page writes it
          <input
            aria-label="Its date, as the page writes it"
            className="mono"
            placeholder="22.03.2026"
            value={dated}
            onChange={(event) => setDated(event.target.value)}
          />
        </label>
      ) : null}
      {option?.takes_stance ? (
        <label>
          What he says to it
          <select
            aria-label="What he says to it"
            value={stance}
            onChange={(event) => setStance(event.target.value as Stance | '')}
          >
            <option value="">Not said</option>
            {STANCES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {option?.takes_english ? (
        <label>
          In one English line
          <input
            aria-label="In one English line"
            maxLength={300}
            value={english}
            onChange={(event) => setEnglish(event.target.value.replace(/[\r\n]+/g, ' '))}
          />
        </label>
      ) : option ? (
        <p className="small muted">
          Kept in its author’s own words only — no line of yours says what it says.
        </p>
      ) : null}
      <label>
        Why it is added
        <input
          aria-label="Why it is added"
          placeholder="the reading missed it, and the charge relies on it"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      <div className="row">
        <button
          type="button"
          className="btn"
          disabled={Boolean(missing) || add.isPending}
          title={missing ?? undefined}
          onClick={() => add.mutate()}
          data-testid={`save-add-line-${row.ordinal}`}
        >
          {add.isPending ? 'Adding…' : 'Add it to the record'}
        </button>
        <button
          type="button"
          className="btn ghost"
          disabled={add.isPending}
          onClick={() => onDone(null)}
        >
          Cancel
        </button>
      </div>
      {missing && !add.isPending ? <p className="small muted">{missing}</p> : null}
      <p className="small muted">
        It goes into the record as your line, with who and why, beside the app’s; the next draft
        reads it.
      </p>
      <ErrorEnvelope error={add.error} />
    </div>
  )
}
